import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Download,
  Refresh,
  Info,
  CheckCircle,
  Schedule,
  Upload
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';

interface APKVersion {
  version: string;
  buildNumber: string;
  downloadUrl: string;
  apkPath: string;
  isActive: boolean;
  releaseDate: string;
  changelog: string;
  packageName?: string;
}

interface EnvironmentAPK {
  id: string;
  name: string;
  environment: string;
  description: string;
  currentVersion: APKVersion;
}

interface Integration {
  id: string;
  name: string;
  description: string;
}

const APKVersionManagerNew: React.FC = () => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [apks, setApks] = useState<EnvironmentAPK[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const environments = [
    { id: 'staging', name: 'STAGING', color: '#2196F3' },
    { id: 'production', name: 'PRODUCTION', color: '#4CAF50' },
    { id: 'beta', name: 'BETA', color: '#FF9800' },
    { id: 'production-new', name: 'PRODUCTION-NEW', color: '#2E7D32' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('APKVersionManagerNew: Starting to load data...');
      console.log('API_BASE:', API_ENDPOINTS.API_BASE);
      console.log('Environments URL:', `${API_ENDPOINTS.API_BASE}/api/tests-new/environments`);
      console.log('Integrations URL:', `${API_ENDPOINTS.API_BASE}/api/tests-new/integrations`);

      const [apksResponse, integrationsResponse] = await Promise.all([
        fetch(`${API_ENDPOINTS.API_BASE}/api/tests-new/environments?t=${Date.now()}`),
        fetch(`${API_ENDPOINTS.API_BASE}/api/tests-new/integrations?t=${Date.now()}`)
      ]);

      console.log('APKVersionManagerNew: Responses received');
      console.log('APKs response status:', apksResponse.status);
      console.log('Integrations response status:', integrationsResponse.status);

      if (!apksResponse.ok || !integrationsResponse.ok) {
        throw new Error(`Failed to load data: APKs=${apksResponse.status}, Integrations=${integrationsResponse.status}`);
      }

      const apksData = await apksResponse.json();
      const integrationsData = await integrationsResponse.json();

      console.log('APKVersionManagerNew: Data parsed successfully');
      console.log('APKs data:', apksData);
      console.log('Integrations data:', integrationsData);

      setApks(apksData);
      setIntegrations(integrationsData);
    } catch (err) {
      console.error('APKVersionManagerNew: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleDownload = (apk: EnvironmentAPK) => {
    console.log('Download APK:', apk.currentVersion.downloadUrl);
    // 使用API端点下载，使用正确的版本号
    const downloadUrl = `${API_ENDPOINTS.API_BASE}/api/apk-versions/${apk.id}/versions/${apk.currentVersion.version}/download`;
    console.log('API Download URL:', downloadUrl);
    window.open(downloadUrl, '_blank');
  };

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAPK, setSelectedAPK] = useState<EnvironmentAPK | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedEnvironment, setSelectedEnvironment] = useState('production');
  const [apkName, setApkName] = useState('');
  const [apkDescription, setApkDescription] = useState('');
  const [apkVersion, setApkVersion] = useState('');
  const [buildNumber, setBuildNumber] = useState('');

  const handleDetails = (apk: EnvironmentAPK) => {
    console.log('View Details for APK:', apk);
    setSelectedAPK(apk);
    setDetailsDialogOpen(true);
  };

  const handleCheckUpdates = async (apk: EnvironmentAPK) => {
    console.log('Check Updates for APK:', apk);
    try {
      // 模拟检查更新API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert(`检查更新完成!\n当前版本: ${apk.currentVersion.version}\n构建号: ${apk.currentVersion.buildNumber}\n\n暂无新版本可用。`);
    } catch (error) {
      console.error('Check updates error:', error);
      alert('检查更新失败，请重试');
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleUpload = () => {
    console.log('Upload APK');
    setUploadDialogOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log('Selected file:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      alert('请选择要上传的APK文件');
      return;
    }

    if (!apkName.trim()) {
      alert('请输入APK名称');
      return;
    }

    if (!apkVersion.trim()) {
      alert('请输入版本号');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('apk', selectedFile);
      formData.append('apkId', `sos2-${selectedEnvironment}`);
      formData.append('version', apkVersion.trim());
      formData.append('environment', selectedEnvironment);
      formData.append('apkName', apkName.trim());
      formData.append('description', apkDescription.trim());
      formData.append('buildNumber', buildNumber.trim() || Date.now().toString().slice(-6));

      const response = await fetch(`${API_ENDPOINTS.API_BASE}/api/apk-versions/upload`, {
        method: 'POST',
        body: formData,
      });

      const contentType = response.headers.get('content-type') || '';
      if (response.ok) {
        let result: any = null;
        try {
          result = contentType.includes('application/json') ? await response.json() : await response.text();
        } catch (e) {
          console.warn('Upload response is not valid JSON, raw text will be used.');
          result = await response.text();
        }
        console.log('Upload successful:', result);
        alert('APK上传成功！');
        
        // 关闭对话框并刷新数据
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setUploadProgress(0);
        setSelectedEnvironment('production');
        setApkName('');
        setApkDescription('');
        setApkVersion('');
        setBuildNumber('');
        
        // 刷新APK数据
        loadData();
      } else {
        let errorText = '';
        try {
          errorText = contentType.includes('application/json') ? JSON.stringify(await response.json()) : await response.text();
        } catch (e) {
          errorText = `HTTP ${response.status}`;
        }
        console.error('Upload failed:', errorText);
        alert(`上传失败: ${errorText}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('上传过程中发生错误，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handlePostReleaseTest = () => {
    console.log('Post Release Test');
    alert('开始发布后测试...\nAPK: 当前APK\n版本: N/A');
  };

  const getEnvironmentAPK = (environmentId: string) => {
    return apks.find(apk => apk.environment === environmentId);
  };

  const renderAPKCard = (environmentId: string) => {
    const apk = getEnvironmentAPK(environmentId);
    const environment = environments.find(env => env.id === environmentId);

    if (!apk || !environment) {
      return (
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary">
              {environment?.name} APK
            </Typography>
            <Typography variant="body2" color="text.secondary">
              暂无数据
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ height: '100%', position: 'relative' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h6" component="div">
                {apk.currentVersion.version || apk.name}
              </Typography>
              {apk.name && apk.name !== apk.currentVersion.version && (
                <Typography variant="body2" color="text.secondary">
                  {apk.name}
                </Typography>
              )}
            </Box>
            <Chip 
              label={environment.name} 
              size="small" 
              sx={{ 
                bgcolor: environment.color,
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Package: {apk.currentVersion.packageName || 'com.infi.sos2'}
          </Typography>

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6" color="primary">
              {apk.currentVersion.version}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              (Build: {apk.currentVersion.buildNumber})
            </Typography>
            <Chip
              icon={<CheckCircle />}
              label="Active"
              color="success"
              size="small"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Released: {apk.currentVersion.releaseDate}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {apk.description}
          </Typography>

          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              size="small"
              startIcon={<Info />}
              variant="outlined"
              color="primary"
              onClick={() => handleDetails(apk)}
            >
              DETAILS
            </Button>
            <Button
              size="small"
              startIcon={<Download />}
              variant="outlined"
              color="primary"
              onClick={() => handleDownload(apk)}
            >
              DOWNLOAD
            </Button>
            <Button
              size="small"
              startIcon={<Refresh />}
              variant="outlined"
              color="primary"
              onClick={() => handleCheckUpdates(apk)}
            >
              CHECK UPDATES
            </Button>
            <Button
              size="small"
              startIcon={<Schedule />}
              variant="outlined"
              color="error"
              onClick={() => handlePostReleaseTest()}
            >
              POST-RELEASE TEST
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderIntegrations = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          集成选项
        </Typography>
        <Grid container spacing={2}>
          {integrations.map((integration) => (
            <Grid item xs={12} sm={6} md={4} key={integration.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {integration.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {integration.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={handleRefresh}>
          重试
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 标题和操作按钮 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          APK Version Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<Schedule />}
            onClick={handlePostReleaseTest}
          >
            POST-RELEASE TEST
          </Button>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={handleUpload}
          >
            UPLOAD APK
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
          >
            REFRESH
          </Button>
        </Box>
      </Box>

      {/* 标签页 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          {environments.map((env, index) => (
            <Tab 
              key={env.id} 
              label={env.name}
              sx={{ 
                color: env.color,
                fontWeight: 'bold'
              }}
            />
          ))}
          <Tab label="INTEGRATIONS" />
        </Tabs>
      </Box>

      {/* 内容区域 */}
      {selectedTab < environments.length ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {renderAPKCard(environments[selectedTab].id)}
          </Grid>
        </Grid>
      ) : (
        renderIntegrations()
      )}

      {/* APK详情弹窗 */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Info />
            APK详情
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAPK && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedAPK.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                环境: {selectedAPK.environment.toUpperCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                版本: {selectedAPK.currentVersion.version}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                构建号: {selectedAPK.currentVersion.buildNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                发布日期: {selectedAPK.currentVersion.releaseDate}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                状态: {selectedAPK.currentVersion.isActive ? 'Active' : 'Inactive'}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                描述:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAPK.currentVersion.changelog}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      {/* 上传APK弹窗 */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Upload />
            上传APK文件
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, border: '2px dashed #ccc', borderRadius: 2, textAlign: 'center' }}>
            <Upload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              拖拽APK文件到此处或点击选择文件
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              支持 .apk 格式文件
            </Typography>
            
            {/* APK信息输入表单 */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="APK名称"
                    value={apkName}
                    onChange={(e) => setApkName(e.target.value)}
                    placeholder="例如: SOS2 Production APK"
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>环境</InputLabel>
                    <Select
                      value={selectedEnvironment}
                      onChange={(e) => setSelectedEnvironment(e.target.value)}
                      label="环境"
                    >
                      <MenuItem value="staging">Staging</MenuItem>
                      <MenuItem value="production">Production</MenuItem>
                      <MenuItem value="beta">Beta</MenuItem>
                      <MenuItem value="production-new">Production-New</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="版本号"
                    value={apkVersion}
                    onChange={(e) => setApkVersion(e.target.value)}
                    placeholder="例如: 2.1.0"
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="构建号"
                    value={buildNumber}
                    onChange={(e) => setBuildNumber(e.target.value)}
                    placeholder="例如: 210 (可选，留空自动生成)"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="描述"
                    value={apkDescription}
                    onChange={(e) => setApkDescription(e.target.value)}
                    placeholder="例如: Production环境稳定版本，包含所有集成功能"
                    multiline
                    rows={2}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
            
            {selectedFile && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="primary">
                  已选择文件: {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  大小: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            )}
            
            <Button
              variant="contained"
              component="label"
              startIcon={<Upload />}
              sx={{ mt: 2 }}
              disabled={uploading}
            >
              选择文件
              <input
                type="file"
                hidden
                accept=".apk"
                onChange={handleFileSelect}
              />
            </Button>
            
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  上传中... {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFile(null);
              setUploadProgress(0);
              setSelectedEnvironment('production');
              setApkName('');
              setApkDescription('');
              setApkVersion('');
              setBuildNumber('');
            }}
            disabled={uploading}
          >
            取消
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUploadSubmit}
            disabled={!selectedFile || uploading}
            startIcon={<Upload />}
          >
            {uploading ? '上传中...' : '上传'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default APKVersionManagerNew;
